import { useEffect, useState } from 'react';
import { useCalculator } from '../contexts/CalculatorContext';
import { useConfig } from '../contexts/ConfigContext';
import { useProject } from '../contexts/ProjectContext';
import { UnifiedCTCTable } from '../components/ConceptTable';
import { MultiSelectDropdown } from '../components/MultiSelectDropdown';
import { exportProjectToExcel } from '../services/excelExportService';
import { OverheadAnalysisCard } from '../components/OverheadAnalysisCard';
import { SaveProjectModal } from '../components/SaveProjectModal';
import { SuccessModal } from '../components/SuccessModal';
import { AnnualTotalCard } from '../components/AnnualTotalCard';
import { exportCalculationToJSON } from '../services/exportService';
import { formatCOP, formatUSD } from '../utils/formatters';
import { getCustomRates } from '../services/customRatesService';
import {
  AlertCircle,
  DollarSign,
  Globe,
  PieChart,
  TrendingUp,
  Settings,
  ChevronDown,
  Calendar,
  Download,
  FolderPlus,
  Trash2
} from 'lucide-react';
import styles from './CalculatorView.module.css';

export function CalculatorView() {
  const { state, setSalary, setConnectivity, setSalaryType, setRiskLevel, setOperativeCosts, calculate, clear } = useCalculator();
  const { config, setGAPercentage } = useConfig();
  const { addResource, setProjectName, state: projectState } = useProject();
  const [isSalaryTypeOpen, setIsSalaryTypeOpen] = useState(false);
  const [isRiskLevelOpen, setIsRiskLevelOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '', subMessage: '' });

  useEffect(() => {
    const timer = setTimeout(() => {
      calculate();
    }, 100);
    return () => clearTimeout(timer);
  }, [state.baseSalary, state.connectivityAllowance, state.salaryType, state.riskLevel, state.selectedOperativeCosts, config.gaPercentage]);

  useEffect(() => {
    calculate();
  }, []);

  const handleExport = async () => {
    if (state.result) {
      // Create a temporary resource object from the current calculation result
      // We need to approximate the additionalCosts structure or reconstruct it
      const concepts = state.result.concepts;

      const additionalCosts = {
        headhunter: 0,
        hiring: concepts.find(c => c.name === 'Hiring')?.amountUSD || 0,
        safetyStudy: concepts.find(c => c.name === 'Safety Study')?.amountUSD || 0,
        laptopRental: concepts.find(c => c.name === 'Laptop Rental')?.amountUSD || 0,
        deliverySupportCost: (concepts.find(c => c.name === 'Delivery support cost')?.percentage || 0) * 100,
        financing: (concepts.find(c => c.name === 'Financing')?.percentage || 0) * 100,
        managementFees: (concepts.find(c => c.name === 'Management Fees')?.percentage || 0) * 100
      };

      // Ensure salaryInfo has all required fields to avoid runtime errors
      const safeSalaryInfo = {
        ...state.result.salaryInfo,
        baseSalary: state.result.salaryInfo.baseSalary || 0
      };

      const tempResource = {
        id: 'temp-resource',
        name: 'Calculated Resource',
        salaryInfo: safeSalaryInfo,
        additionalCosts: additionalCosts,
        monthlyCTC: state.result
      };

      try {
        await exportProjectToExcel({
          project: {
            id: 'single-calc',
            name: projectState.projectName || 'CTC Calculation',
            userId: 'current-user',
            resources: [tempResource],
            createdAt: new Date(),
            updatedAt: new Date(),
            projection: null
          },
          config: config
        });
      } catch (error) {
        console.error(error);
        alert("Error al exportar Excel");
      }
    }
  };

  const handleSaveToProject = () => {
    if (!state.result) return;
    setIsSaveModalOpen(true);
  };

  const handleModalSave = async (resourceName: string, projectName: string, applicantName: string) => {
    if (!state.result) return;

    // Update global project name locally for UI consistency immediately
    setProjectName(projectName);

    // Prepare resource data extracting from calculated concepts
    const concepts = state.result.concepts;
    const additionalCosts = {
      headhunter: 0,
      hiring: concepts.find(c => c.name === 'Hiring')?.amountUSD || 0,
      safetyStudy: concepts.find(c => c.name === 'Safety Study')?.amountUSD || 0,
      laptopRental: concepts.find(c => c.name === 'Laptop Rental')?.amountUSD || 0,
      deliverySupportCost: (concepts.find(c => c.name === 'Delivery support cost')?.percentage || 0) * 100,
      financing: 0,
      managementFees: 0
    };

    try {
      // Add to project with persistence
      // @ts-ignore
      await addResource(resourceName, projectName, state.result.salaryInfo, additionalCosts, state.result, applicantName);

      setIsSaveModalOpen(false);

      // Show success modal
      setSuccessModal({
        isOpen: true,
        title: '¡Recurso Guardado!',
        message: `"${resourceName}" se ha guardado correctamente.`,
        subMessage: `Encontrado en proyecto: ${projectName}`
      });
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Error al guardar el recurso. Por favor intente nuevamente.');
    }
  };

  const getSalaryTypeLabel = () => {
    if (!state.result) return '';
    return state.result.salaryInfo.type === 'integral' ? 'Salario Integral' : 'Salario Ordinario';
  };

  const base = state.result?.concepts.filter(c => c.category === 'base') || [];
  const prestaciones = state.result?.concepts.filter(c => c.category === 'prestaciones') || [];
  const seguridad = state.result?.concepts.filter(c => c.category === 'seguridad') || [];
  const parafiscales = state.result?.concepts.filter(c => c.category === 'parafiscales') || [];
  const beneficios = state.result?.concepts.filter(c => c.category === 'beneficios') || [];
  const costOfLaw = state.result?.concepts.filter(c => c.category === 'costOfLaw') || [];
  const operativos = state.result?.concepts.filter(c => c.category === 'operativos') || [];
  const subtotalCost = state.result?.concepts.filter(c => c.category === 'subtotalCost') || [];
  const adicionales = state.result?.concepts.filter(c => c.category === 'adicionales') || [];
  const totalCost = state.result?.concepts.filter(c => c.category === 'totalCost') || [];

  const monthlyProjection = state.result ? Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    let monthlyCostUSD = state.result!.totalUSD;
    let monthlyCostCOP = state.result!.totalCOP;

    // Subtract one-time costs for months after the first one
    if (month > 1 && state.result?.concepts) {
      const concepts = state.result.concepts;

      // Calculate one-time base amounts
      const oneTimeUSD = concepts
        .filter(c => c.name === 'Hiring' || c.name === 'Safety Study')
        .reduce((sum, c) => sum + c.amountUSD, 0);

      const oneTimeCOP = concepts
        .filter(c => c.name === 'Hiring' || c.name === 'Safety Study')
        .reduce((sum, c) => sum + c.amountCOP, 0);

      if (oneTimeUSD > 0) {
        // Calculate the markup multiplier that was applied to these costs
        const financingRate = concepts.find(c => c.name === 'Financing')?.percentage || 0;
        const gaRate = concepts.find(c => c.name === 'G&A Colombia')?.percentage || 0;
        const managementRate = concepts.find(c => c.name === 'Management Fees')?.percentage || 0;

        // Markups are additive on the subtotal
        const totalMarkup = 1 + financingRate + gaRate + managementRate;

        // Subtract the one-time cost plus its markup from the monthly total
        monthlyCostUSD -= (oneTimeUSD * totalMarkup);
        monthlyCostCOP -= (oneTimeCOP * totalMarkup);
      }
    }

    return { month, totalUSD: monthlyCostUSD, totalCOP: monthlyCostCOP };
  }) : [];

  const totalAnualUSD = monthlyProjection.reduce((sum, m) => sum + m.totalUSD, 0);
  const totalAnualCOP = monthlyProjection.reduce((sum, m) => sum + m.totalCOP, 0);

  return (
    <div className={styles.container}>
      {/* Compact Input Card */}
      <div className={styles.configCard}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <Settings size={18} className={styles.configIcon} />
            <h2 className={styles.configTitle}>Configuración de Nómina</h2>
          </div>
          {state.result && (
            <div className={styles.actionButtons}>
              <button className={styles.exportBtn} onClick={handleExport}>
                <Download size={16} />
                <span>Exportar</span>
              </button>
              <button className={styles.saveBtn} onClick={handleSaveToProject}>
                <FolderPlus size={16} />
                <span>Guardar</span>
              </button>
              <button className={styles.clearBtn} onClick={() => setSalary(0)}>
                <Trash2 size={16} />
                <span>Limpiar</span>
              </button>
            </div>
          )}
        </div>

        <div className={styles.inputGrid}>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Salario Base Mensual</label>
            <div className={styles.inputWrapper}>
              <span className={styles.inputPrefix}>$</span>
              <input
                type="number"
                value={state.baseSalary || ''}
                onChange={(e) => setSalary(Number(e.target.value))}
                className={styles.input}
                placeholder="Ej: 5000000"
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Costos Adicionales</label>
            <MultiSelectDropdown
              options={[
                { value: 'Connectivity assistance', label: 'Auxilio de Conectividad' },
                { value: 'separator', label: 'Costos Operativos', isSeparator: true },
                { value: 'Hiring', label: 'Hiring' },
                { value: 'Safety Study', label: 'Safety Study' },
                { value: 'Laptop Rental', label: 'Laptop Rental' },
                { value: 'Delivery support cost', label: 'Delivery Support Cost' },
              ]}
              selected={state.selectedOperativeCosts}
              onChange={(selected) => {
                setOperativeCosts(selected);
                // Update connectivity allowance based on selection
                if (selected.includes('Connectivity assistance')) {
                  const customRates = getCustomRates(state.salaryType === 'auto' ? 'ordinario_inf' : state.salaryType);
                  setConnectivity(customRates['Connectivity assistance'] || 0);
                } else {
                  setConnectivity(0);
                }
              }}
              placeholder="Seleccionar costos..."
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Modalidad de Contrato</label>
            <div className={styles.selectWrapper}>
              <select
                value={state.salaryType}
                onChange={(e) => setSalaryType(e.target.value as any)}
                onFocus={() => setIsSalaryTypeOpen(true)}
                onBlur={() => setIsSalaryTypeOpen(false)}
                className={styles.select}
              >
                <option value="ordinario_inf">Ordinario Inferior</option>
                <option value="ordinario_sup">Ordinario Superior</option>
                <option value="integral">Salario Integral</option>
                <option value="auto">Automático</option>
              </select>
              <ChevronDown className={`${styles.selectIcon} ${isSalaryTypeOpen ? styles.selectIconOpen : ''}`} size={16} />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Nivel de Riesgo ARL</label>
            <div className={styles.selectWrapper}>
              <select
                value={state.riskLevel}
                onChange={(e) => setRiskLevel(Number(e.target.value))}
                onFocus={() => setIsRiskLevelOpen(true)}
                onBlur={() => setIsRiskLevelOpen(false)}
                className={styles.select}
              >
                <option value={0.522}>I (0.522%) - Admin/Financiero</option>
                <option value={1.044}>II (1.044%) - Ventas/Servicios</option>
                <option value={2.436}>III (2.436%) - Manufactura</option>
                <option value={4.350}>IV (4.350%) - Transporte</option>
                <option value={6.960}>V (6.960%) - Construcción</option>
              </select>
              <ChevronDown className={`${styles.selectIcon} ${isRiskLevelOpen ? styles.selectIconOpen : ''}`} size={16} />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Margen G&A (%)</label>
            <div className={styles.inputWrapper}>
              <input
                type="number"
                min="0"
                max="50"
                value={config.gaPercentage}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val >= 0 && val <= 50) {
                    setGAPercentage(val);
                  }
                }}
                className={styles.input}
                style={{ paddingRight: '32px', paddingLeft: '12px' }}
              />
              <span className={styles.inputPrefix} style={{ left: 'auto', right: '12px' }}>%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      {state.result && (
        <div className={styles.mainContentLayout}>
          {/* Left Column - Results */}
          <div className={styles.leftColumn}>
            <div className={styles.resultsSection}>
              <div className={styles.totalSection}>
                <div className={`${styles.totalCard} ${styles.totalCardCOP}`}>
                  <div className={styles.cardBlur}></div>
                  <div className={styles.cardIconBg}>
                    <DollarSign size={128} />
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.cardHeader}>
                      <PieChart size={16} className={styles.cardHeaderIcon} />
                      <h3 className={styles.cardTitle}>Total Monthly Cost - {getSalaryTypeLabel()}</h3>
                    </div>
                    <div className={styles.cardAmount}>
                      {formatCOP(state.result.totalCOP)}
                      <span className={styles.cardCurrency}>COP</span>
                    </div>
                  </div>
                  <div className={styles.cardFooter}>
                    <div className={styles.cardDot}></div>
                    Incluye carga prestacional + G&A
                  </div>
                </div>

                <div className={`${styles.totalCard} ${styles.totalCardUSD}`}>
                  <div className={styles.cardBlur}></div>
                  <div className={styles.cardIconBg}>
                    <Globe size={128} />
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.cardHeader}>
                      <TrendingUp size={16} className={styles.cardHeaderIcon} />
                      <h3 className={styles.cardTitle}>Total Monthly Cost - {getSalaryTypeLabel()}</h3>
                    </div>
                    <div className={styles.cardAmount}>
                      {formatUSD(state.result.totalUSD)}
                      <span className={styles.cardCurrency}>USD</span>
                    </div>
                  </div>
                  <div className={styles.cardFooter}>
                    <div className={styles.cardDot}></div>
                    Conversión a tasa TRM actual
                  </div>
                </div>
              </div>

              <div className={styles.breakdown}>
                <UnifiedCTCTable
                  sections={[
                    { title: 'Salario Base', concepts: base },
                    { title: 'Prestaciones Sociales', concepts: prestaciones },
                    { title: 'Seguridad Social', concepts: seguridad },
                    { title: 'Parafiscales', concepts: parafiscales },
                    { title: 'Otros Beneficios', concepts: beneficios },
                    { title: '', concepts: costOfLaw },
                    { title: 'Costos Operativos', concepts: operativos },
                    { title: '', concepts: subtotalCost },
                    { title: 'Costos Adicionales', concepts: adicionales },
                    { title: '', concepts: totalCost },
                  ]}
                  currentSalaryType={state.result.effectiveSalaryType}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Projection Panel */}
          <div className={styles.rightColumn}>
            <OverheadAnalysisCard
              salaryType={state.result?.effectiveSalaryType || state.salaryType}
              smmlv={config.smmlv}
              gaPercentage={config.gaPercentage}
              baseSalary={state.baseSalary}
            />
            <AnnualTotalCard
              totalAnualUSD={totalAnualUSD}
              totalAnualCOP={totalAnualCOP}
            />
            <div className={styles.projectionPanel}>
              <div className={styles.projectionHeader}>
                <Calendar size={18} />
                <h3>Proyección por Año</h3>
              </div>


              <div className={styles.projectionTable}>
                <div className={styles.projectionTableHeader}>
                  <span>Mes</span>
                  <span>COS</span>
                  <span>G&A</span>
                  <span>Monthly Price</span>
                </div>
                {monthlyProjection.map((month) => {
                  const cos = month.totalUSD / (1 + config.gaPercentage / 100);
                  const ga = month.totalUSD - cos;
                  return (
                    <div key={month.month} className={styles.projectionRow}>
                      <span className={styles.projectionMonth}>{month.month}</span>
                      <span className={styles.projectionRowValue}>
                        {cos.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                      </span>
                      <span className={styles.projectionRowValue}>
                        {ga.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                      </span>
                      <span className={styles.projectionRowTotal}>
                        {month.totalUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className={styles.projectionSummary}>
                <div className={styles.summaryRow}>
                  <span>Total Anual</span>
                  <span>{formatUSD(totalAnualUSD)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <SaveProjectModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={handleModalSave}
        defaultProjectName={projectState.projectName}
      />
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ ...successModal, isOpen: false })}
        title={successModal.title}
        message={successModal.message}
        subMessage={successModal.subMessage}
      />
    </div>
  );
}
